import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import {
  Plus,
  Pencil,
  Trash2,
  Info,
  ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";

import { api, type PricingRule } from "@/lib/api";

// ── helpers ──────────────────────────────────────────────────────────────────

function pct(v: number) {
  return `${(v * 100).toFixed(1)}%`;
}

type RoundingValue = "x.99" | "x.00" | "none";

const ROUNDING_LABELS: Record<RoundingValue, string> = {
  "x.99": "x.99",
  "x.00": "x.00",
  none: "none",
};

function scopeLabel(rule: PricingRule): string {
  if (rule.scope_brand && rule.scope_category)
    return `${rule.scope_brand} / ${rule.scope_category}`;
  if (rule.scope_brand) return `Brand: ${rule.scope_brand}`;
  if (rule.scope_category) return `Cat: ${rule.scope_category}`;
  return "Global";
}

// ── Form state ───────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  scope_brand: string;
  scope_category: string;
  min_margin: string;   // display as "12" → API 0.12
  target_margin: string;
  rounding: RoundingValue;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  name: "",
  scope_brand: "",
  scope_category: "",
  min_margin: "12",
  target_margin: "20",
  rounding: "x.99",
  active: true,
};

function ruleToForm(rule: PricingRule): FormState {
  return {
    name: rule.name,
    scope_brand: rule.scope_brand ?? "",
    scope_category: rule.scope_category ?? "",
    min_margin: (rule.min_margin * 100).toFixed(1),
    target_margin: (rule.target_margin * 100).toFixed(1),
    rounding: (rule.rounding as RoundingValue) ?? "none",
    active: rule.active,
  };
}

function formToPayload(f: FormState): Omit<PricingRule, "id" | "version"> {
  return {
    name: f.name.trim(),
    scope_brand: f.scope_brand.trim() || null,
    scope_category: f.scope_category.trim() || null,
    min_margin: parseFloat(f.min_margin) / 100,
    target_margin: parseFloat(f.target_margin) / 100,
    rounding: f.rounding,
    active: f.active,
  };
}

// ── Rule form sheet ───────────────────────────────────────────────────────────

interface RuleSheetProps {
  open: boolean;
  editing: PricingRule | null;
  onClose: () => void;
}

function RuleSheet({ open, editing, onClose }: RuleSheetProps) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Sync form when sheet opens
  const [lastEditingId, setLastEditingId] = useState<string | null>(null);
  if (open && editing && editing.id !== lastEditingId) {
    setForm(ruleToForm(editing));
    setLastEditingId(editing.id);
  }
  if (open && !editing && lastEditingId !== null) {
    setForm(EMPTY_FORM);
    setLastEditingId(null);
  }

  const set = (key: keyof FormState, val: FormState[keyof FormState]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const createMut = useMutation({
    mutationFn: (payload: Omit<PricingRule, "id" | "version">) =>
      api.createPricingRule(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pricing-rules"] });
      onClose();
    },
  });

  const patchMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<PricingRule> }) =>
      api.patchPricingRule(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pricing-rules"] });
      onClose();
    },
  });

  const isPending = createMut.isPending || patchMut.isPending;
  const error = createMut.error ?? patchMut.error;

  function handleSave() {
    const payload = formToPayload(form);
    if (editing) {
      patchMut.mutate({ id: editing.id, patch: payload });
    } else {
      createMut.mutate(payload);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0 overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>{editing ? "Edit rule" : "New pricing rule"}</SheetTitle>
          <SheetDescription>
            {editing
              ? "Update the rule — changes apply on the next pricing run."
              : "Most-specific matching rule wins (brand › category › global)."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4 flex-1">
          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="rule-name">
              Name
            </label>
            <Input
              id="rule-name"
              placeholder="e.g. Dell laptops base"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          {/* Scope */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="rule-brand">
                Brand scope
              </label>
              <Input
                id="rule-brand"
                placeholder="e.g. Dell (empty = any)"
                value={form.scope_brand}
                onChange={(e) => set("scope_brand", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="rule-category">
                Category scope
              </label>
              <Input
                id="rule-category"
                placeholder="e.g. Laptops (empty = any)"
                value={form.scope_category}
                onChange={(e) => set("scope_category", e.target.value)}
              />
            </div>
          </div>

          {/* Margins */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="rule-min-margin">
                Min margin %
              </label>
              <div className="relative">
                <Input
                  id="rule-min-margin"
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  placeholder="12"
                  value={form.min_margin}
                  onChange={(e) => set("min_margin", e.target.value)}
                  className="pr-6"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  %
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="rule-target-margin">
                Target margin %
              </label>
              <div className="relative">
                <Input
                  id="rule-target-margin"
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  placeholder="20"
                  value={form.target_margin}
                  onChange={(e) => set("target_margin", e.target.value)}
                  className="pr-6"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  %
                </span>
              </div>
            </div>
          </div>

          {/* Rounding */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="rule-rounding">
              Rounding
            </label>
            <select
              id="rule-rounding"
              value={form.rounding}
              onChange={(e) => set("rounding", e.target.value as RoundingValue)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="x.99">x.99 — charm pricing</option>
              <option value="x.00">x.00 — whole dollar</option>
              <option value="none">none — exact</option>
            </select>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="rule-active"
              checked={form.active}
              onCheckedChange={(v) => set("active", v === true)}
            />
            <label htmlFor="rule-active" className="text-sm font-medium cursor-pointer">
              Active
            </label>
            <span className="text-xs text-muted-foreground ml-1">
              Inactive rules are stored but never matched.
            </span>
          </div>

          {/* Error */}
          {error && (
            <p className="text-xs text-rose-500 rounded-md bg-rose-500/10 px-3 py-2 border border-rose-500/20">
              {error instanceof Error ? error.message : "Save failed."}
            </p>
          )}
        </div>

        <SheetFooter className="mt-6 pt-4 border-t border-border">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending || !form.name.trim()}>
            {isPending ? "Saving…" : editing ? "Save changes" : "Create rule"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ── Delete confirm inline ─────────────────────────────────────────────────────

function DeleteCell({ rule }: { rule: PricingRule }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const deleteMut = useMutation({
    mutationFn: () => api.deletePricingRule(rule.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pricing-rules"] });
      setConfirming(false);
    },
  });

  if (confirming) {
    return (
      <span className="flex items-center gap-1">
        <Button
          size="sm"
          variant="destructive"
          className="h-6 px-2 text-xs"
          onClick={() => deleteMut.mutate()}
          disabled={deleteMut.isPending}
        >
          {deleteMut.isPending ? "…" : "Delete"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          onClick={() => setConfirming(false)}
        >
          Cancel
        </Button>
      </span>
    );
  }

  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-500"
      onClick={() => setConfirming(true)}
    >
      <Trash2 className="h-3.5 w-3.5" />
      <span className="sr-only">Delete</span>
    </Button>
  );
}

// ── Active toggle cell ────────────────────────────────────────────────────────

function ActiveToggleCell({ rule }: { rule: PricingRule }) {
  const qc = useQueryClient();
  const patchMut = useMutation({
    mutationFn: (active: boolean) =>
      api.patchPricingRule(rule.id, { active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pricing-rules"] });
    },
  });

  return (
    <Checkbox
      checked={rule.active}
      disabled={patchMut.isPending}
      onCheckedChange={(v) => patchMut.mutate(v === true)}
      aria-label={rule.active ? "Deactivate rule" : "Activate rule"}
    />
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PricingPage() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["pricing-rules"],
    queryFn: api.listPricingRules,
  });

  const rules = data?.items ?? [];

  const columns: ColumnDef<PricingRule>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <span className="font-medium text-sm">{row.original.name}</span>
      ),
    },
    {
      id: "scope",
      header: "Scope",
      cell: ({ row }) => {
        const label = scopeLabel(row.original);
        const isGlobal = label === "Global";
        return (
          <Badge
            variant={isGlobal ? "secondary" : "outline"}
            className="font-mono text-xs"
          >
            {label}
          </Badge>
        );
      },
    },
    {
      accessorKey: "min_margin",
      header: "Min margin",
      cell: ({ row }) => (
        <span className="tabular-nums text-sm text-muted-foreground">
          {pct(row.original.min_margin)}
        </span>
      ),
    },
    {
      accessorKey: "target_margin",
      header: "Target margin",
      cell: ({ row }) => (
        <span className="tabular-nums text-sm font-medium text-emerald-500">
          {pct(row.original.target_margin)}
        </span>
      ),
    },
    {
      accessorKey: "rounding",
      header: "Rounding",
      cell: ({ row }) => (
        <span className="text-xs font-mono text-muted-foreground">
          {ROUNDING_LABELS[(row.original.rounding as RoundingValue) ?? "none"] ?? row.original.rounding ?? "—"}
        </span>
      ),
    },
    {
      id: "active",
      header: "Active",
      cell: ({ row }) => <ActiveToggleCell rule={row.original} />,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <span className="flex items-center gap-1 justify-end">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            onClick={() => {
              setEditingRule(row.original);
              setSheetOpen(true);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="sr-only">Edit</span>
          </Button>
          <DeleteCell rule={row.original} />
        </span>
      ),
    },
  ];

  const table = useReactTable({
    data: rules,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pricing engine</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Margin-first rules applied at listing time. Most specific match wins.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingRule(null);
            setSheetOpen(true);
          }}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          New rule
        </Button>
      </div>

      {/* Info card */}
      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-blue-400">
            <Info className="h-4 w-4 shrink-0" />
            Rule matching order
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-1">
          <p>
            A product is matched against all active rules. The most specific active rule wins:
          </p>
          <ol className="list-decimal list-inside space-y-0.5 pl-1">
            <li>
              <span className="font-medium text-foreground">Brand + Category scoped</span> — narrowest, highest priority
            </li>
            <li>
              <span className="font-medium text-foreground">Brand scoped</span> — matches any category for that brand
            </li>
            <li>
              <span className="font-medium text-foreground">Category scoped</span> — matches any brand in that category
            </li>
            <li>
              <span className="font-medium text-foreground">Global</span> (no brand, no category) — fallback
            </li>
          </ol>
          <p className="pt-1 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 text-blue-400 shrink-0" />
            If no rule matches, the pricing engine uses the system floor (cost × 1.03).
          </p>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex flex-col gap-2 p-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-10 rounded-md bg-muted/40 shimmer" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-rose-500 text-center">
              Failed to load pricing rules.
            </div>
          ) : rules.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No rules yet.{" "}
              <button
                className="underline hover:text-foreground transition-colors"
                onClick={() => {
                  setEditingRule(null);
                  setSheetOpen(true);
                }}
              >
                Create the first one.
              </button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="text-xs uppercase tracking-wide text-muted-foreground"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-2.5">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Rule form sheet */}
      <RuleSheet
        open={sheetOpen}
        editing={editingRule}
        onClose={() => {
          setSheetOpen(false);
          setEditingRule(null);
        }}
      />
    </div>
  );
}
